import { rgba } from 'polished';
import React, { FC, forwardRef } from 'react';
import Select, { Props, StylesConfig as Styles, Theme } from 'react-select';
import colors from 'themes/default/common/colors';
import { rem } from 'utils/fonts';

export const baseStyle: Styles<{}, false> = {
  control: styles => ({
    ...styles,
    height: 60,
    border: `1px solid ${colors.inputBorderColor}`,
    borderRadius: 9,
    paddingLeft: 10,
    fontWeight: 500,
    fontSize: rem('16px'),
    color: colors.greyLinkActive,
    zIndex: 998,
  }),
  group: styles => ({
    ...styles,
    borderRadius: 9,
    paddingTop: 0,
    zIndex: 999,
  }),
  menu: styles => ({
    ...styles,
    zIndex: 999,
    borderRadius: 9,
    paddingTop: 0,
    boxShadow: `0px 14.2045px 28.4091px ${rgba(colors.gray[900], 0.08)}`,
  }),
  menuList: styles => ({
    ...styles,
    paddingTop: 0,
    zIndex: 999,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    fontSize: rem('15px'),
    margin: 0,
    padding: '12px 20px',
    fontWeight: 600,
    backgroundColor: isSelected ? colors.pink[500] : isFocused ? rgba(colors.pink[500], 0.1) : undefined,
    color: isSelected ? 'white' : colors.greyLinkActive,
    //@ts-ignore
    ':active': {
      //@ts-ignore
      ...styles[':active'],
      backgroundColor: isSelected ? colors.pink[500] : rgba(colors.pink[500], 0.3),
    },
  }),
  placeholder: styles => ({ ...styles, color: colors.gray[400], fontSize: rem(15), whiteSpace: 'nowrap' }),
};

export const underlinedStyles: Styles<{}, false> = {
  container: styles => ({ ...styles, width: ' 100%', height: 40 }),
  control: (styles, { isFocused }) => ({
    ...styles,
    border: 'none',
    boxShadow: 'none',
    borderBottom: `1px solid ${isFocused ? colors.pink[500] : colors.gray[200]}`,
    borderRadius: 0,
    '& > div': {
      paddingLeft: 0,
    },
  }),
  noOptionsMessage: styles => ({ ...styles, fontWeight: 600, color: colors.gray[900], fontSize: rem('15px') }),
  input: styles => ({ ...styles, fontWeight: 600, height: 31, color: colors.gray[900], fontSize: rem('15px') }),
  singleValue: styles => ({ ...styles, fontWeight: 600, color: colors.gray[900], fontSize: rem('15px') }),
  placeholder: styles => ({ ...styles, color: colors.gray[400], fontWeight: 600, fontSize: rem('15px') }),
  menu: styles => ({ ...styles, border: 0, boxShadow: `0px 14px 28px ${rgba(colors.gray[900], 0.08)}` }),
  option: baseStyle.option,
};

export const borderlessStyles: Styles<{}, false> = {
  container: styles => ({ ...styles, width: ' 100%', height: 38 }),
  control: styles => ({
    ...styles,
    border: 'none',
    boxShadow: 'none',
    borderRadius: 0,
    cursor: 'pointer',
    '& > div': {
      paddingLeft: 20,
    },
  }),
  noOptionsMessage: styles => ({ ...styles, fontWeight: 500, color: colors.gray[900], fontSize: rem('15px') }),
  input: styles => ({ ...styles, fontWeight: 500, height: 31, color: colors.gray[900], fontSize: rem('15px') }),
  singleValue: styles => ({ ...styles, fontWeight: 500, color: colors.gray[900], fontSize: rem('15px') }),
  placeholder: styles => ({ ...styles, color: colors.gray[400], fontWeight: 500, fontSize: rem('15px') }),
  menu: styles => ({ ...styles, border: 0, boxShadow: `0px 14px 28px ${rgba(colors.gray[900], 0.08)}` }),
  option: baseStyle.option,
};

export const borderlessStylesWithFocus: Styles<{}, false> = {
  ...borderlessStyles,
  container: styles => ({ ...styles, minWidth: '145px', width: 'auto', height: 40, border: 0 }),
  control: (styles, { isFocused, menuIsOpen }) => ({
    ...styles,
    border: 0,
    cursor: 'pointer',

    backgroundColor: isFocused || menuIsOpen ? rgba(colors.pink[200], 0.4) : 'transparent',
    '& > div': {
      paddingLeft: 14,
    },
    '&:hover': {
      backgroundColor: rgba(colors.pink[200], 0.4),
    },
  }),
};

export const modalStyles: Styles<{}, false> = {
  ...baseStyle,
  menuPortal: styles => ({ ...styles, zIndex: 9999 }),
};

export const modalStylesWithError: Styles<{}, false> = {
  ...modalStyles,
  control: styles => ({
    ...styles,
    height: 60,
    border: `1px solid ${colors.pink[500]}`,
    borderRadius: 9,
    paddingLeft: 10,
    fontWeight: 500,
    fontSize: rem('16px'),
    color: colors.greyLinkActive,
    zIndex: 999,
  }),
};

export const peopleListStyles: Styles<{}, false> = {
  ...underlinedStyles,
  menuPortal: styles => ({ ...styles, zIndex: 9999, width: 160 }),
  option: styles => ({
    ...styles,
    fontSize: rem('13px'),
    margin: 0,
    padding: '6px 10px',
  }),
  input: styles => ({ ...styles, fontSize: rem('13px') }),
  singleValue: styles => ({ ...styles, fontSize: rem('13px') }),
  placeholder: styles => ({ ...styles, fontSize: rem('13px') }),
  control: (styles, { isFocused }) => ({
    ...styles,
    border: 'none',
    boxShadow: 'none',
    borderBottom: `1px solid ${isFocused ? colors.pink[500] : colors.gray[200]}`,
    borderRadius: 0,
    backgroundColor: 'transparent',
    '& > div': {
      paddingLeft: 0,
    },
  }),
};

// eslint-disable-next-line react/display-name
const SearchableSelect: FC<Props<{}, false>> = forwardRef((props, ref) => {
  return (
    <Select
      // @ts-ignore
      ref={ref}
      components={{
        IndicatorSeparator: null,
      }}
      theme={(theme: Theme): Theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: colors.pink[500],
        },
      })}
      styles={baseStyle}
      {...props}
    />
  );
});

export default SearchableSelect;
